<template>
  <div>
    <div class="container pax20">
      <div class="pv2">
        <nuxt-link :to="localePath({ name: 'index' })" class="mr1"
          ><img
            src="~/assets/icons/ic_home.svg"
            width="16"
            height="16"
            class="v-mid"
        /></nuxt-link>
        <img
          src="~/assets/icons/ic_arrow_bc.svg"
          width="6"
          height="10"
          class="mr1"
        />
        <span class="breadcrumb-silver mr1">{{
          $t('navigation.services')
        }}</span>
        <img
          src="~/assets/icons/ic_arrow_bc.svg"
          width="6"
          height="10"
          class="mr1"
        />
        <nuxt-link
          :to="
            localePath({
              name: 'services-city',
              params: { city: $route.params.city },
            })
          "
          class="mr1 breadcrumb-silver no-underline"
          >{{ $t(`cities.${currentCountry}.${cityKey}`) }}</nuxt-link
        >
        <img
          src="~/assets/icons/ic_arrow_bc.svg"
          width="6"
          height="10"
          class="mr1"
        />
        {{ $t(service.localizedTitle[currentLang]) }}
      </div>
      <h1>
        {{ $t(service.localizedTitle[currentLang]) }}
        {{ $t('cities.skillPageTitle', { city: cityLocationTranslation }) }}
      </h1>
      <div v-if="skills.length === 0">
        {{ $t('common.serviceNotAvailable') }}
      </div>
      <div class="flex flex-wrap nl1 nl3-l">
        <nuxt-link
          v-for="skill in skills"
          :key="skill.title"
          :to="
            localePath({
              name: 'services-city-service-skill',
              params: {
                city: $route.params.city,
                service: $route.params.service,
                skill: skill.slug,
              },
            })
          "
          class="nuxt-link db w-50 w-third-l pa1 pa3-l overflow-hidden no-underline"
        >
          <div class="bg-white overflow-hidden br3 card mb4">
            <div class="service-card">
              <img :data-src="skill.image" class="image-cover lazyload" />
            </div>
            <div class="pa3">
              <p class="f4 fw5 mv2">{{ skill.localizedTitle[currentLang] }}</p>
              <p v-if="skill.areaPriceStats" class="mv1">
                {{ $t('common.from') }}
                <Amount :value="skill.areaPriceStats" />{{ ' / ' }}
                {{ $t('common.perHour') }}
              </p>
            </div>
          </div>
        </nuxt-link>
      </div>
    </div>
    <div class="bg-near-white">
      <DownloadNow />
    </div>
  </div>
</template>

<script>
import marked from 'marked';
import slug from 'slug';

import DownloadNow from '~/components/DownloadNow';
import Amount from '~/components/Amount';
import utils from '~/plugins/utilities';
import api from '~/services/api';

async function getServicesWithSkills(context) {
  const services = [];
  const skills = [];
  const response = await api.getServices(context);
  Object.values(response.services).forEach((data) => {
    services.push(data);
  });
  Object.values(response.skills)
    .sort((d2, d1) => (d1.sortPriority || 0) - (d2.sortPriority || 0))
    .forEach((data) => {
      // if (!data.hidden) {
      skills.push(data);
    });
  return { services, skills };
}

function resolveCity(cityKey, app, locale) {
  return slug(app.i18n.messages[locale].cities[locale.split('-')[1]][cityKey], {
    locale: locale.split('-')[0],
  });
}

export default {
  scrollToTop: true,
  components: {
    DownloadNow,
    Amount,
  },
  async validate(context) {
    const { params, app } = context;
    const lang = app.i18n.locale.split('-')[0];
    const country = app.i18n.locale.split('-')[1];
    const cityParam = params.city;
    const cities = app.i18n.messages[app.i18n.locale].cities[country];
    const cityKey = utils.getCityCodeFromRouterParam(cityParam, app.i18n);
    const response = await getServicesWithSkills(context);
    context.services = response.services;
    context.skills = response.skills;
    let service;

    context.services.forEach((item) => {
      const trans = item.localizedTitle[lang];
      if (!trans) {
        return;
      }
      // sluggify and match translated title
      const sluggify = slug(trans, { locale: lang });
      if (sluggify === params.service) {
        // return service if slug matches
        service = item;
      }
    });
    context.service = service;
    if (service && cityKey && cityKey in cities) {
      return true;
    } else {
      return false;
    }
  },
  async asyncData(context) {
    const { params, app, store, $sentry } = context;
    try {
      const lang = app.i18n.locale.split('-')[0];
      const country = app.i18n.locale.split('-')[1];
      const cityParam = params.city;
      // get city key from translated param
      const cityKey = utils.getCityCodeFromRouterParam(cityParam, app.i18n);
      const service = context.service;
      // translate different language routes for language switcher
      const transParams = await new Promise((resolve) => {
        const routesToResolve = {};
        Object.keys(app.i18n.messages).forEach((key) => {
          if (key.split('-')[1] === country) {
            const lang = key.split('-')[0];
            if (service.localizedTitle[lang]) {
              routesToResolve[key] = {
                city: resolveCity(cityKey, app, key),
                service: slug(service.localizedTitle[lang], { locale: lang }),
              };
            }
          }
        });
        resolve(routesToResolve);
      });
      store.dispatch('i18n/setRouteParams', transParams);

      // get skills with services
      const skills = [];
      context.skills.forEach((data) => {
        if (
          !data.hidden &&
          data.areaPriceStats &&
          data.serviceId === service.id &&
          data.areaPriceStats[cityKey]
        ) {
          if (data.localizedTitle[lang]) {
            skills.push({
              title: data.title,
              localizedTitle: data.localizedTitle,
              image: data.image,
              areaPriceStats: data.areaPriceStats[cityKey].from,
              slug: slug(data.localizedTitle[lang], { locale: lang }),
            });
          }
        }
      });
      return { service, skills, cityKey };
    } catch (error) {
      $sentry.captureException(error);
      throw error;
    }
  },
  data() {
    return {
      service: {},
      skills: [],
      cityKey: '',
    };
  },
  computed: {
    currentLang() {
      return this.$i18n.locale.split('-')[0];
    },
    currentCountry() {
      return this.$i18n.locale.split('-')[1];
    },
    cityLocationTranslation() {
      const country = this.$i18n.locale.split('-')[1];
      const key = 'cities.' + country + '-location.' + this.cityKey;
      const translation = this.$t(key);
      if (translation === key) {
        return this.$t('cities.' + country + '.' + this.cityKey);
      } else {
        return translation;
      }
    },
  },
  methods: {
    htmlFromMd(text) {
      return marked(text);
    },
  },
  head() {
    const lang = this.$i18n.locale.split('-')[0];
    return {
      title:
        this.$t(this.service.localizedTitle[lang]) +
        ' ' +
        this.$t('cities.skillPageTitle', {
          city: this.cityLocationTranslation,
        }),
      meta: [
        // hid is used as unique identifier. Do not use `vmid` for it as it will not work
        {
          hid: 'description',
          name: 'description',
          content: '',
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: '',
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content:
            this.$t(this.service.localizedTitle[lang]) +
            ' ' +
            this.$t('cities.skillPageTitle', {
              city: this.cityLocationTranslation,
            }),
        },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `https://discontract.com${this.$route.path.replace(
            /\/?$/,
            '/'
          )}`,
        },
      ],
    };
  },
};
</script>
